import { logDebugMessage } from './debugConsole';

const FORCED_TRANSLATION_DEBUG_USER_IDS = new Set([
  '136908068', // 高俊亨
]);

type LanguageOccurrences = Record<string, number>;

export type ChatLanguageDecisionSummary = {
  detectableCount: number;
  foreignCount: number;
  foreignRatio: number;
  hasForeignMessages: boolean;
  mostFrequentLanguage?: string;
};

export type AutoTranslateDecision =
  | 'request_chat_translation'
  | 'skip_auto_translate_disabled'
  | 'skip_chat_translation_disabled'
  | 'skip_language_matches_target'
  | 'skip_foreign_ratio_below_threshold'
  | 'skip_do_not_translate_language'
  | 'skip_chat_language_unknown';

export type AutoTranslateDecisionContext = {
  canAutoTranslate: boolean;
  detectedChatLanguage?: string;
  doNotTranslate?: string[];
  hasForeignMessages?: boolean;
  isTranslationDisabled?: boolean;
  shouldAutoTranslate: boolean;
  targetLanguage: string;
};

export const MIN_FOREIGN_RATIO = 0.1;

export function normalizeTranslationLanguageCode(lang?: string | null) {
  if (!lang) return undefined;
  const dash = lang.indexOf('-');
  return (dash > 0 ? lang.substring(0, dash) : lang).toLowerCase();
}

export function shouldForceTranslationDebugForUser(userId?: string) {
  return Boolean(userId && FORCED_TRANSLATION_DEBUG_USER_IDS.has(userId));
}

export function summarizeChatLanguageDecision(
  languageOccurrences: LanguageOccurrences,
  targetLanguage?: string,
): ChatLanguageDecisionSummary {
  const detectableCount = Object.values(languageOccurrences).reduce((sum, count) => sum + count, 0);
  const sortedLanguages = Object.entries(languageOccurrences)
    .sort(([, a], [, b]) => b - a);
  const mostFrequentLanguage = sortedLanguages[0]?.[0];
  const normalizedTarget = normalizeTranslationLanguageCode(targetLanguage);

  let foreignCount = 0;
  if (normalizedTarget) {
    for (const [lang, count] of Object.entries(languageOccurrences)) {
      if (normalizeTranslationLanguageCode(lang) !== normalizedTarget) {
        foreignCount += count;
      }
    }
  }

  const foreignRatio = detectableCount > 0 ? foreignCount / detectableCount : 0;

  return {
    detectableCount,
    foreignCount,
    foreignRatio,
    hasForeignMessages: Boolean(normalizedTarget) && foreignRatio >= MIN_FOREIGN_RATIO,
    mostFrequentLanguage,
  };
}

export function explainAutoTranslateDecision(
  context: AutoTranslateDecisionContext,
): AutoTranslateDecision {
  if (!context.shouldAutoTranslate) {
    return 'skip_auto_translate_disabled';
  }

  if (context.isTranslationDisabled) {
    return 'skip_chat_translation_disabled';
  }

  if (context.canAutoTranslate) {
    return 'request_chat_translation';
  }

  const normalizedTarget = normalizeTranslationLanguageCode(context.targetLanguage);
  const normalizedDetected = normalizeTranslationLanguageCode(context.detectedChatLanguage);

  if (!normalizedDetected) {
    return 'skip_chat_language_unknown';
  }

  if (context.doNotTranslate?.includes(normalizedDetected)) {
    return 'skip_do_not_translate_language';
  }

  if (context.hasForeignMessages === false && normalizedDetected === normalizedTarget) {
    return 'skip_language_matches_target';
  }

  return 'skip_foreign_ratio_below_threshold';
}

export function shouldDiscardTranslationAsOriginal(params: {
  originalText?: string;
  translatedText?: string;
}) {
  return Boolean(
    params.originalText
    && params.translatedText
    && params.originalText === params.translatedText,
  );
}

export function logTranslationDebugEvent(
  isEnabled: boolean | undefined,
  event: string,
  payload: Record<string, unknown>,
) {
  if (!isEnabled) return;
  logDebugMessage('debug', '[translation-debug]', {
    event,
    ...payload,
  });
}
